import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Box, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, IconButton, Chip, Tab, Tabs
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import config from '../../config';

export const AlertModal = ({ open, onClose, symbol }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedSymbol, setSelectedSymbol] = useState(symbol || 'AAPL');
  const [indicator, setIndicator] = useState('SIGNAL');
  const [targetSignal, setTargetSignal] = useState('BUY');
  const [condition, setCondition] = useState('ABOVE');
  const [targetValue, setTargetValue] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (symbol) setSelectedSymbol(symbol);
  }, [symbol]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Token ${token}` } : {};
  };

  const fetchUserAlerts = useCallback(async () => {
    try {
      const params = symbol ? { symbol } : {};
      const res = await axios.get(`${config.API_BASE_URL}api/technical-analysis/useralerts/`, {
        headers: getAuthHeaders(),
        params
      });
      setAlerts(res.data);
    } catch (err) {
      console.error('Failed to fetch user alerts', err);
    }
  }, [symbol]);

  useEffect(() => {
    if (open) fetchUserAlerts();
  }, [open, fetchUserAlerts]);

  const handleCreateAlert = async () => {
    if (!selectedSymbol.trim()) {
      alert('Please enter or select an instrument symbol.');
      return;
    }

    if (indicator !== 'SIGNAL' && (targetValue === '' || isNaN(targetValue))) {
      alert('Please provide a valid Target Value.');
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${config.API_BASE_URL}api/technical-analysis/useralerts/`,
        {
          symbol: selectedSymbol.toUpperCase(),
          indicator: indicator,
          condition: indicator === 'SIGNAL' ? 'EQUALS' : condition,
          target_signal: indicator === 'SIGNAL' ? targetSignal : null,
          target_value: indicator !== 'SIGNAL' ? parseFloat(targetValue) : null,
        },
        { headers: getAuthHeaders() }
      );
      setTargetValue('');
      await fetchUserAlerts();
      setTabIndex(1);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not save alert trigger. Check connection or login status.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAlert = async (id) => {
    try {
      await axios.delete(`${config.API_BASE_URL}api/technical-analysis/useralerts/${id}/`, {
        headers: getAuthHeaders()
      });
      fetchUserAlerts();
    } catch (err) {
      console.error('Failed to delete alert', err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ bg: '#0F172A', color: '#FFF' }}>
        {symbol ? `Manage Alerts for ${symbol}` : 'Manage All Instrument Alerts'}
      </DialogTitle>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={tabIndex} onChange={(e, val) => setTabIndex(val)}>
          <Tab label="Create New Trigger" />
          <Tab label={`Active Alerts (${alerts.length})`} />
        </Tabs>
      </Box>

      <DialogContent>
        {tabIndex === 0 ? (
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              fullWidth
              required
              label="Instrument Symbol"
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              disabled={Boolean(symbol)}
              placeholder="e.g. AAPL, BTCUSDT, TSLA"
            />

            <TextField select fullWidth label="Trigger Type" value={indicator} onChange={(e) => setIndicator(e.target.value)}>
              <MenuItem value="SIGNAL">Quantitative Signal Change (BUY / SELL)</MenuItem>
              <MenuItem value="RSI">RSI Threshold Cross</MenuItem>
              <MenuItem value="PRICE">Price Target Break</MenuItem>
            </TextField>

            {indicator === 'SIGNAL' ? (
              <TextField select fullWidth label="Target Signal State" value={targetSignal} onChange={(e) => setTargetSignal(e.target.value)}>
                <MenuItem value="BUY">BUY Signal Triggered</MenuItem>
                <MenuItem value="SELL">SELL Signal Triggered</MenuItem>
              </TextField>
            ) : (
              <Box display="flex" gap={2}>
                <TextField select fullWidth label="Condition" value={condition} onChange={(e) => setCondition(e.target.value)}>
                  <MenuItem value="ABOVE">Greater Than (&gt;)</MenuItem>
                  <MenuItem value="BELOW">Less Than (&lt;)</MenuItem>
                </TextField>
                <TextField 
                  fullWidth 
                  required
                  type="number" 
                  label="Target Value" 
                  value={targetValue} 
                  onChange={(e) => setTargetValue(e.target.value)} 
                  placeholder={indicator === 'RSI' ? 'e.g. 70' : 'e.g. 320.5'} 
                />
              </Box>
            )}

            <Button variant="contained" size="large" onClick={handleCreateAlert} disabled={loading} sx={{ mt: 1, height: 48 }}>
              {loading ? 'Saving...' : 'Save Alert Trigger'}
            </Button>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {!symbol && <TableCell>Symbol</TableCell>}
                <TableCell>Indicator</TableCell>
                <TableCell>Condition</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.map((a) => (
                <TableRow key={a.id}>
                  {!symbol && <TableCell><strong>{a.symbol}</strong></TableCell>}
                  <TableCell><Chip label={a.indicator} size="small" /></TableCell>
                  <TableCell>{a.indicator === 'SIGNAL' ? `Is ${a.target_signal}` : `${a.condition} ${a.target_value}`}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => handleDeleteAlert(a.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {alerts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={symbol ? 3 : 4} align="center">
                    <Typography color="textSecondary" py={2}>
                      {symbol ? `No active alerts found for ${symbol}` : 'No active alerts found across any instruments'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};